import type {
  BaseJsxgraphDisplayConfig,
  BehaviorConfig,
  CoordinateSystemConfig,
  WidgetConfig,
} from '@examdojo/core/widget';
import type { ReactionGroup } from '@examdojo/core/widget';
import type { FeatureStyleNode } from './elements/features';

// =============================================================================
// ELEMENT TYPES
// =============================================================================

/** Function element types supported by Graphpad */
export type FunctionType =
  | 'linear'
  | 'quadratic'
  | 'quadratic-vertex'
  | 'sine'
  | 'cosine'
  | 'exponential'
  | 'logarithmic'
  | 'absolute'
  | 'cubic'
  | 'cubic-general'
  | 'sqrt'
  | 'tangent-fn'
  | 'rational'
  | 'rational-general';

/** Feature types - computed from one or more elements */
export type FeatureType =
  | 'roots'
  | 'extrema'
  | 'tangent'
  | 'secant'
  | 'derivative'
  | 'intersection'
  | 'handle'
  | 'yIntercept'
  | 'asymptotes'
  | 'inflection'
  | 'axisOfSymmetry'
  | 'trajectory'
  | 'monotonicity'
  | 'domain'
  | 'range'
  | 'functionArea'
  | 'intersectionArea'
  | 'amplitude'
  | 'wavelength'
  | 'period'
  | 'phaseShift';

/** Primitive types - direct JSXGraph primitives with params and display */
export type PrimitiveType = 'label' | 'point' | 'line' | 'polygon';

/** All possible element types in the system */
export type ElementType = FunctionType | FeatureType | PrimitiveType;

// =============================================================================
// HANDLE BEHAVIORS
// =============================================================================

/** Handle drag behaviors */
/* In the future we could add more handle behaviors, like rotate, scale, etc. */
export type HandleBehavior =
  | 'translate' // Move freely in x and y
  | 'translate-x' // Move only horizontally
  | 'translate-y'; // Move only vertically

// =============================================================================
// ELEMENT DEFINITIONS (PARAMS)
// Pure data definitions - no display information
// =============================================================================

/** Base element definition */
export interface BaseElementDef {
  type: ElementType;
}

/** Function element definition */
export interface FunctionElementDef<T extends FunctionType = FunctionType> extends BaseElementDef {
  type: T;
  params?: Record<string, ValueExpression>;
}

/** Feature element definition - references one or more elements */
export interface FeatureElementDef<T extends FeatureType = FeatureType> extends BaseElementDef {
  type: T;
  /** Feature-specific parameters (including `of` dependency reference) */
  params?: Record<string, unknown>;
}

/**
 * Handle element definition.
 * @deprecated Handle is now a feature type. Use FeatureElementDef<'handle'> instead.
 */
export type HandleElementDef = FeatureElementDef<'handle'>;

// =============================================================================
// VALUE EXPRESSIONS
// A number or a string expression resolved via ValueContext
// =============================================================================

/** A value that is either a literal number or a ValueContext expression string */
export type ValueExpression = number | string;

// =============================================================================
// POSITION REFERENCES
// =============================================================================

/** A position defined by x/y coordinates — literal numbers or ValueContext expressions */
export interface PositionRef {
  x: ValueExpression;
  y: ValueExpression;
}

// =============================================================================
// PRIMITIVE ELEMENT DEFINITIONS
// Direct JSXGraph primitives as graphpad elements
// =============================================================================

/** Label element params — the text content IS the element's identity */
export interface LabelElementParams {
  /** The text content (supports value templates like `{f.a}`, `{f-tangent.slope}`) */
  text: string;
  /** Decimal precision for numeric values in templates */
  precision?: number;
  /** Attach to another element (like features use `of` for functions) */
  of?: string;
  /** Standalone position (when not attached via `of`) */
  position?: PositionRef;
}

/** Point element params — a standalone point */
export interface PointElementParams {
  x: ValueExpression;
  y: ValueExpression;
  name?: string;
}

/** Line element params — a line between two positions */
export interface LineElementParams {
  from: PositionRef;
  to: PositionRef;
  name?: string;
}

/** Polygon element params — vertices forming a polygon */
export interface PolygonElementParams {
  vertices: PositionRef[];
  name?: string;
  vertexNames?: string[];
}

/** Primitive element definition */
export interface PrimitiveElementDef extends BaseElementDef {
  type: PrimitiveType;
  params: LabelElementParams | PointElementParams | LineElementParams | PolygonElementParams;
}

export type ElementDef = FunctionElementDef | FeatureElementDef | PrimitiveElementDef;

// =============================================================================
// PARAMS - The Model
// Pure mathematical/logical definitions without any visual configuration
// =============================================================================

/** Element definitions keyed by ID */
export type GraphpadParams = Record<string, ElementDef>;

// =============================================================================
// DISPLAY - The View
// All visual configuration: board appearance + element styling
// =============================================================================

/**
 * Display configuration for an individual element.
 * Uses the FeatureStyleNode pattern for recursive, nested styling.
 *
 * ElementDisplayConfig IS a FeatureStyleNode, enabling:
 * - Element-level styles that cascade to all parts
 * - Subpart-specific overrides
 * - Arbitrarily deep nesting
 *
 * @example
 * ```typescript
 * // Simple element styling
 * const pointConfig: ElementDisplayConfig = {
 *   point: { color: 'sky' },
 * };
 *
 * // Feature with subparts
 * const extremaConfig: ElementDisplayConfig = {
 *   point: {},
 *   subparts: {
 *     maxima: { point: { color: 'green' } },
 *     minima: { point: { color: 'red' } },
 *   },
 * };
 * ```
 */
export type ElementDisplayConfig = FeatureStyleNode;

/** Complete display configuration for Graphpad */
export interface GraphpadDisplayConfig extends BaseJsxgraphDisplayConfig {
  /** Coordinate system configuration (axes, grid, bounding box) */
  coordinateSystem?: CoordinateSystemConfig;
  /** Display configuration for individual elements, keyed by element ID */
  elements?: Record<string, ElementDisplayConfig>;
}

// =============================================================================
// BEHAVIOR CONFIG
// =============================================================================

/** Graphpad-specific behavior configuration with reactive conditions */
export interface GraphpadBehaviorConfig extends BehaviorConfig {
  /** Reactive condition groups that dispatch actions when ValueContext conditions change */
  reactions?: ReactionGroup[];
}

// =============================================================================
// WIDGET CONFIG
// =============================================================================

/** Complete Graphpad widget configuration */
export type GraphpadWidgetConfig = WidgetConfig<
  'graphpad',
  GraphpadParams,
  GraphpadDisplayConfig,
  GraphpadBehaviorConfig
>;

// =============================================================================
// TYPE GUARDS
// =============================================================================

const FUNCTION_TYPES: FunctionType[] = [
  'linear',
  'quadratic',
  'quadratic-vertex',
  'sine',
  'cosine',
  'exponential',
  'logarithmic',
  'absolute',
  'cubic',
  'cubic-general',
  'sqrt',
  'tangent-fn',
  'rational',
  'rational-general',
];
const FEATURE_TYPES: FeatureType[] = [
  'roots',
  'extrema',
  'tangent',
  'secant',
  'derivative',
  'intersection',
  'handle',
  'yIntercept',
  'asymptotes',
  'inflection',
  'axisOfSymmetry',
  'trajectory',
  'monotonicity',
  'domain',
  'range',
  'functionArea',
  'intersectionArea',
  'amplitude',
  'wavelength',
  'period',
  'phaseShift',
];

const PRIMITIVE_TYPES: PrimitiveType[] = ['label', 'point', 'line', 'polygon'];

export function isFunctionElementDef(def: ElementDef): def is FunctionElementDef {
  return FUNCTION_TYPES.includes(def.type as FunctionType);
}

export function isFeatureElementDef(def: ElementDef): def is FeatureElementDef {
  return FEATURE_TYPES.includes(def.type as FeatureType);
}

export function isPrimitiveElementDef(def: ElementDef): def is PrimitiveElementDef {
  return PRIMITIVE_TYPES.includes(def.type as PrimitiveType);
}

export function isHandleElementDef(def: ElementDef): def is HandleElementDef {
  return def.type === 'handle';
}
